import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const Urgency = {
    Low: "L",
    Medium: "M",
    High: "H"
};
export type UrgencyType = typeof Urgency[keyof typeof Urgency];
export const Impact = {
    Low: "L",
    Medium: "M",
    High: "H"
};
export type ImpactType = typeof Impact[keyof typeof Impact];
export const AssignedToType = {
    User: "U",
    Group: "G"
};
export type AssignedToTypeType = typeof AssignedToType[keyof typeof AssignedToType];
class TemplateTask extends Base implements EntityType {
    templateTaskId: uuid;
    title: string;
    duration?: number | null;
    urgency: UrgencyType;
    impact: ImpactType;
    categories: uuid[];
    assignedToType: AssignedToTypeType;
    assignedTo?: uuid | null;
    global: boolean;
    activityTypeIds: uuid[];
    billable: boolean;
    costOverride: number;
    isCostOverridden: boolean;
    noCharge: boolean;
    hideFromInvoice: boolean;
    invoiceNotes?: string | null;
    favoritedBy: uuid[];
    sources: string[];
    targets: string[];
    constructor(doc: typeof TemplateTask | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            templateTaskId: genUUID(),
            title: null,
            urgency: null,
            impact: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "templateTask";
        // @ts-ignore
        this.templateTaskId = doc._templateTaskId || doc.templateTaskId || doc.templatetaskid || genUUID();
        // @ts-ignore
        this.title = doc._title || doc.title || null;
        // @ts-ignore
        this.duration = util.coalesce(util.parseFloat(doc._duration), util.parseFloat(doc.duration), util.parseFloat(doc.duration), null);
        // @ts-ignore
        this.urgency = util.trimRight(doc._urgency) || util.trimRight(doc.urgency) || null;
        // @ts-ignore
        this.impact = util.trimRight(doc._impact) || util.trimRight(doc.impact) || null;
        // @ts-ignore
        this.categories = doc._categories || doc.categories || [];
        // @ts-ignore
        this.assignedToType = util.trimRight(doc._assignedToType) || util.trimRight(doc.assignedToType) || util.trimRight(doc.assignedtotype) || "U";
        // @ts-ignore
        this.assignedTo = doc._assignedTo || doc.assignedTo || doc.assignedto || null;
        // @ts-ignore
        this.global = util.defaultBoolean([doc.global, doc._global, doc.global, true]);
        // @ts-ignore
        this.activityTypeIds = doc._activityTypeIds || doc.activityTypeIds || doc.activitytypeids || [];
        // @ts-ignore
        this.billable = util.defaultBoolean([doc.billable, doc._billable, doc.billable, false]);
        // @ts-ignore
        this.costOverride = util.coalesce(util.parseFloat(doc._costOverride), util.parseFloat(doc.costOverride), util.parseFloat(doc.costoverride), 0);
        // @ts-ignore
        this.isCostOverridden = util.defaultBoolean([doc.isCostOverridden, doc._isCostOverridden, doc.iscostoverridden, false]);
        // @ts-ignore
        this.noCharge = util.defaultBoolean([doc.noCharge, doc._noCharge, doc.nocharge, false]);
        // @ts-ignore
        this.hideFromInvoice = util.defaultBoolean([doc.hideFromInvoice, doc._hideFromInvoice, doc.hidefrominvoice, false]);
        // @ts-ignore
        this.invoiceNotes = doc._invoiceNotes || doc.invoiceNotes || doc.invoicenotes || null;
        // @ts-ignore
        this.favoritedBy = doc._favoritedBy || doc.favoritedBy || doc.favoritedby || [];
        // @ts-ignore
        this.sources = doc._sources || doc.sources || [];
        // @ts-ignore
        this.targets = doc._targets || doc.targets || [];
    }
    get id(): uuid { return this.templateTaskId; }
    static URGENCY_VALUES = ["L", "M", "H"];
    static IMPACT_VALUES = ["L", "M", "H"];
    addCategory(category: uuid): uuid {
        if (!category) throw new ValidationError('No nulls allowed in an array!');
        if (!this.categories) this.categories = [];
        this.categories.push(category);
        return category;
    }
    updateCategory(category: uuid) {
        if (!this.categories) this.categories = [];
        const index = this.categories.findIndex(item => item === category);
        if (index !== -1) { this.categories[index] = category; }
        else this.addCategory(category);
    }
    removeCategory(category: uuid) {
        if (!this.categories) return;
        let foundItems = this.categories?.filter(item => item === category);
        if (foundItems && foundItems.length > 0) {
            this.categories = this.categories.filter(item => !(item === category));
        }
    }
    abandonCategories() { this.categories = []; }
    static ASSIGNED_TO_TYPE_VALUES = ["U", "G"];
    addActivityTypeId(activityTypeId: uuid): uuid {
        if (!activityTypeId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.activityTypeIds) this.activityTypeIds = [];
        this.activityTypeIds.push(activityTypeId);
        return activityTypeId;
    }
    updateActivityTypeId(activityTypeId: uuid) {
        if (!this.activityTypeIds) this.activityTypeIds = [];
        const index = this.activityTypeIds.findIndex(item => item === activityTypeId);
        if (index !== -1) { this.activityTypeIds[index] = activityTypeId; }
        else this.addActivityTypeId(activityTypeId);
    }
    removeActivityTypeId(activityTypeId: uuid) {
        if (!this.activityTypeIds) return;
        let foundItems = this.activityTypeIds?.filter(item => item === activityTypeId);
        if (foundItems && foundItems.length > 0) {
            this.activityTypeIds = this.activityTypeIds.filter(item => !(item === activityTypeId));
        }
    }
    abandonActivityTypeIds() { this.activityTypeIds = []; }
    addFavoritedBy(favoritedBy: uuid): uuid {
        if (!favoritedBy) throw new ValidationError('No nulls allowed in an array!');
        if (!this.favoritedBy) this.favoritedBy = [];
        this.favoritedBy.push(favoritedBy);
        return favoritedBy;
    }
    updateFavoritedBy(favoritedBy: uuid) {
        if (!this.favoritedBy) this.favoritedBy = [];
        const index = this.favoritedBy.findIndex(item => item === favoritedBy);
        if (index !== -1) { this.favoritedBy[index] = favoritedBy; }
        else this.addFavoritedBy(favoritedBy);
    }
    removeFavoritedBy(favoritedBy: uuid) {
        if (!this.favoritedBy) return;
        let foundItems = this.favoritedBy?.filter(item => item === favoritedBy);
        if (foundItems && foundItems.length > 0) {
            this.favoritedBy = this.favoritedBy.filter(item => !(item === favoritedBy));
        }
    }
    abandonFavoritedBy() { this.favoritedBy = []; }
    addSource(source: string): string {
        if (!source) throw new ValidationError('No nulls allowed in an array!');
        if (!this.sources) this.sources = [];
        this.sources.push(source);
        return source;
    }
    updateSource(source: string) {
        if (!this.sources) this.sources = [];
        const index = this.sources.findIndex(item => item === source);
        if (index !== -1) { this.sources[index] = source; }
        else this.addSource(source);
    }
    removeSource(source: string) {
        if (!this.sources) return;
        let foundItems = this.sources?.filter(item => item === source);
        if (foundItems && foundItems.length > 0) {
            this.sources = this.sources.filter(item => !(item === source));
        }
    }
    abandonSources() { this.sources = []; }
    addTarget(target: string): string {
        if (!target) throw new ValidationError('No nulls allowed in an array!');
        if (!this.targets) this.targets = [];
        this.targets.push(target);
        return target;
    }
    updateTarget(target: string) {
        if (!this.targets) this.targets = [];
        const index = this.targets.findIndex(item => item === target);
        if (index !== -1) { this.targets[index] = target; }
        else this.addTarget(target);
    }
    removeTarget(target: string) {
        if (!this.targets) return;
        let foundItems = this.targets?.filter(item => item === target);
        if (foundItems && foundItems.length > 0) {
            this.targets = this.targets.filter(item => !(item === target));
        }
    }
    abandonTargets() { this.targets = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for a templateTask"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.templateTaskId; }
    static override getNonRefArrayFields(): string[] { return ["categories", "activityTypeIds", "favoritedBy", "sources", "targets"]; }
    static override getDefaultEntries(): TemplateTask[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'templateTask',
    dataArea: 'Tasks and Time',
    attachedTo: {parents: ['user', 'group'], via: 'assignedToType', onDelete: 'delete', onReplace: 'move'},
    table: 'TEMPLATE_TASK',
    // Keep deleted rows in the client store and backup (historical references). OWA-68
    includeDeletedInResults: true,
    comment: 'A reusable task template that firm staff can instantiate into real tasks on demand. Templates carry all task metadata — title, urgency, impact, duration, billing settings, assignee, categories, and activity types — but no due date. They can be firm-wide (global) or private to their creator, and are attached to case-type stages so the app auto-creates tasks when a case enters that stage.',
    fields: {
        templateTaskId: {type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for a templateTask'},
        title: {
            type: 'string', required: true, comment: 'A one-line description of the templateTask, starting with a verb'
        },
        duration: {
            type: 'float', comment: 'How long will it take to finish this templateTask (in hours)?',
        },
        urgency: {
            type: 'char', maxLength: 1, required: true, values: ['L', 'M', 'H'], valueNames: ['Low', 'Medium', 'High'],
            comment: 'How urgent is this templateTask?',
        },
        impact: {
            type: 'char', maxLength: 1, required: true, values: ['L', 'M', 'H'], valueNames: ['Low', 'Medium', 'High'],
            comment: 'What is the impact of successfully completing this templateTask?',
        },

        categories: {
            type: 'uuid[]', entityType: 'category', comment: 'To which categories is this templateTask related?',
        },

        /**
         * Are we assigning this to a user or a group?
         */
        assignedToType: {
            type: 'char',
            values: ['U', 'G'], // TemplateTask or Group
            valueNames: ['User', 'Group'],
            default: 'U',
            entity: {
                map: {U: 'user', G: 'group'}, // This is a mapping of key field to entityType
                value: 'assignedTo',
            },
            comment: 'Are we assigning this templateTask to a user or a group?'
        },

        /**
         * The user or group this templateTask is assigned to.
         */
        assignedTo: {
            type: 'uuid',
            comment: 'User or group this templateTask is assigned to',
        },

        /**
         * This makes the template global, false means private to user
         */
        global: {
            type: 'boolean',
            default: true,
            comment: 'Shared with everyone at the firm by default; turn off to keep it private to its creator'
        },

        // From activity
        activityTypeIds: {
            type: 'uuid[]', entityType: 'activityType', comment: 'The ids of the related activity types'
        },

        billable: {
            // Defaults true for Client work, false for all other, but can be overridden
            type: 'boolean',
            comment: 'Is this a billable activity? If so, all of the following fields will be displayed'
        },

        // Billable Fields - Only those needed for a flat cost, and if the time is noCharge/hideFromInvoice
        costOverride: {type: 'money', default: 0, comment: 'Manually overridden cost'},

        isCostOverridden: {type: 'boolean', comment: 'Is the cost being overridden, either manually, as a flat fee, or through a noCharge?'},

        noCharge: {type: 'boolean', comment: 'Do not charge anything for this entry'},

        hideFromInvoice: {type: 'boolean', comment: 'Should this entry be hidden from the invoice?'},

        invoiceNotes: {type: 'string', comment: 'Public notes for the invoice'},

        favoritedBy: {type: 'uuid[]', entityType: 'user', comment: 'Users that have favorited this template'},

        /* Transients */
        sources: {type: 'string[]', transient: true, comment: 'Unused, can delete'},

        targets: {type: 'string[]', transient: true, comment: 'Unused, can delete'},
    },
    indexes: [
        {name: 'notDeleted', fn: t => t.status !== 'D'},
    ],
    maps: [
        {name: 'categories', keysFn: t => t.categories, presentFn: (t, k) => t.categories?.includes(k)},
        {name: 'activityTypeIds', keysFn: t => t.activityTypeIds, presentFn: (t, k) => t.activityTypeIds?.includes(k)},
        {name: 'favoritedBy', keysFn: t => t.favoritedBy, presentFn: (t, k) => t.favoritedBy?.includes(k)},
    ]
});
    }
}
export default TemplateTask;
